import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../context/SessionContext";
import { Doctor } from "../../types/auth.types";
import { OnboardingResponse } from "../../types/billing.types";
import { capitalizeFullName } from "../../lib/formatters";
import { onboardingService } from "../../services/onboarding";
import { doctorService } from "../../services/doctors";
import { useApi } from "../../hooks/useApi";
import { marcarBillingDesatualizado } from "../../lib/checkoutIntent";
import { showSuccess } from "../../lib/feedback";
import { crmError, formatCrmInput } from "../../lib/validators";
import { PATHS, checkoutPath, paymentInvitePath } from "../../app/paths";

type OnboardingField = "nome" | "crm" | "especialidade";

export const useOnboarding = (doctor: Doctor | null | undefined) => {
  const { user } = useAuth0();
  const { metadata, setMetadata, setBilling, setPapel } = useSession();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [ehSecretaria, setEhSecretaria] = useState(false);
  const navigate = useNavigate();
  const onboardingApi = useApi<OnboardingResponse>();
  const perfilApi = useApi<Doctor | null>();

  // `metadata.email` só é garantido depois de um bootstrap "ready" — quem
  // chega aqui pode ser um usuário totalmente novo (sem registro de médico
  // ainda, então nunca passou por essa hidratação). O e-mail de sessão vem
  // sempre do Auth0, nunca de `doctor` (que pode ser `null`), então
  // sincroniza aqui pra todo o resto do app poder contar com
  // `metadata.email` assim que o onboarding terminar.
  useEffect(() => {
    if (!user?.email) return;
    setMetadata((prev) => (prev.email ? prev : { ...prev, email: user.email! }));
  }, [user?.email, setMetadata]);

  // Pre-populate the form with whatever the backend already has for this doctor
  useEffect(() => {
    if (!doctor) return;
    setMetadata((prev) => ({
      ...prev,
      nome: prev.nome || doctor.nome,
      especialidade: prev.especialidade || doctor.especialidade,
      total_pacotes_cirurgia: Number(doctor.total_pacotes_cirurgia),
      total_pacotes_opme: Number(doctor.total_pacotes_opme),
      total_recursos: Number(doctor.total_recursos),
      total_resumo_com_referencias: Number(doctor.total_resumo_com_referencias),
      role: prev.role || doctor.role,
    }));
  }, [doctor, setMetadata]);

  // Secretária não se declara: quem define isso é o médico, ao vincular o
  // e-mail dela em Configurações. `GET /doctors/{email}` resolve um e-mail de
  // secretária devolvendo o perfil do médico com `role="secretary"` — é assim
  // que descobrimos, sem perguntar nada a ela.
  //
  // Não dá pra usar o `doctor` do bootstrap aqui: ele só é buscado depois de
  // `GET /billing/assinatura`, que dá 404 justamente pra quem ainda não tem
  // cadastro — ou seja, chega sempre `null` nesta tela.
  useEffect(() => {
    const email = user?.email;
    if (!email) return;
    let cancelled = false;

    doctorService
      .get(perfilApi.apiFetch, email)
      .then((perfil) => {
        if (!cancelled && perfil?.role === "secretary") setEhSecretaria(true);
      })
      .catch(() => {
        // 404 = e-mail desconhecido, o caso normal de um médico novo. Sem
        // toast: não é erro, é só ausência de vínculo.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Secretária só precisa do nome; médico precisa também de CRM e especialidade.
  useEffect(() => {
    const completo = ehSecretaria
      ? Boolean(metadata.nome)
      : Boolean(metadata.nome && metadata.crm && !crmError(metadata.crm) && metadata.especialidade);
    setIsSubmitDisabled(!completo);
  }, [ehSecretaria, metadata.nome, metadata.crm, metadata.especialidade]);

  const handleInputChange = (field: OnboardingField, value: string) => {
    const formattedValue = field === "nome" ? capitalizeFullName(value) : field === "crm" ? formatCrmInput(value) : value;
    setMetadata((prev) => ({ ...prev, [field]: formattedValue }));
  };

  const makeRedirect = async () => {
    if (isSubmitDisabled) return;
    setIsSubmitDisabled(true);

    // Sem `email` no payload — identidade vem sempre do token (ver
    // OnboardingRequest/POST /onboarding), então não há nada pra checar aqui.
    const payload = {
      tipo: ehSecretaria ? ("secretaria" as const) : ("medico" as const),
      nome: metadata.nome,
      especialidade: ehSecretaria ? undefined : metadata.especialidade,
      crm: ehSecretaria ? undefined : metadata.crm,
    };

    // Convite de acesso (/entrar/:token): entra na clínica do convite em vez
    // de criar organização própria. Checado ANTES do onboarding normal porque
    // é o mesmo formulário — só muda o destino.
    const acessoPendente = sessionStorage.getItem("acessoPendente");

    const result = await onboardingApi.request(
      () =>
        acessoPendente
          ? onboardingService.postEntrarPorConvite(onboardingApi.apiFetch, acessoPendente, payload)
          : onboardingService.post(onboardingApi.apiFetch, payload)
    );

    if (result && acessoPendente) {
      sessionStorage.removeItem("acessoPendente");
      sessionStorage.removeItem("acessoConfirmado");
    }

    if (!result) {
      // toast.error já foi disparado pelo hook com a mensagem certa
      setIsSubmitDisabled(false);
      return;
    }
    showSuccess("onboardingSaved");

    // Atualiza contexto com o que foi enviado + o que o onboarding retornou
    setMetadata((prev) => ({
      ...prev,
      email: prev.email || user?.email || "",
      nome: metadata.nome,
      crm: ehSecretaria ? prev.crm : metadata.crm,
      especialidade: ehSecretaria ? prev.especialidade : metadata.especialidade,
      role: ehSecretaria ? "secretaria" : "medico",
    }));
    setBilling({
      nivel: result.direitos.nivel,
      aviso: result.direitos.aviso,
      acesso_ate: result.direitos.acesso_ate,
      limites: result.direitos.limites,
      uso: {},
      features: {
        voz: result.direitos.nivel === "completo",
        ocr: result.direitos.nivel === "completo",
      },
      uso_free: {},
    });
    setPapel(result.papel);
    // O billing acima é parcial (sem plano_chave, plano, trial_disponivel...):
    // força o ProtectedRoute a buscar o GET /billing/assinatura completo, senão
    // quem entra por convite vê "Básico" até dar refresh.
    marcarBillingDesatualizado();

    // Entrou por convite de acesso: o convite acabou de ser consumido, então
    // voltar pra `returnTo` (/entrar/:token) refaria o GET do resumo e cairia
    // em "convite inválido" mesmo com a entrada tendo dado certo. Vai direto
    // pro app.
    if (acessoPendente) {
      sessionStorage.removeItem("returnTo");
      navigate(PATHS.newOrder, { replace: true });
      return;
    }

    // Se o onboarding foi disparado a partir de /assinar/:token (convite de
    // venda assistida), retoma o convite agora que a organização existe —
    // checado antes de `planoPendente` de propósito: são mutuamente
    // exclusivos, mas se algum dia coexistirem, convite vence por vir de uma
    // ação mais específica (link nominal, possivelmente com desconto).
    const convitePendente = sessionStorage.getItem("convitePendente");
    if (convitePendente) {
      sessionStorage.removeItem("convitePendente");
      navigate(paymentInvitePath(convitePendente), { replace: true });
      return;
    }

    // Se o onboarding foi disparado a partir de /assinar (usuário sem
    // organização tentando assinar um plano), retoma o checkout agora que a
    // organização existe.
    const planoPendente = sessionStorage.getItem("planoPendente");
    if (planoPendente) {
      sessionStorage.removeItem("planoPendente");
      navigate(checkoutPath(planoPendente), { replace: true });
      return;
    }

    const returnTo = sessionStorage.getItem("returnTo");
    sessionStorage.removeItem("returnTo");
    navigate(returnTo || PATHS.newOrder, { replace: true });
  };

  return {
    metadata,
    ehSecretaria,
    isSubmitDisabled,
    handleInputChange,
    makeRedirect,
  };
};
